/**
 * The confirmation before a row's delete: the record's name in the title, the
 * standard warning, Cancel and a destructive Delete. It runs the request the
 * caller supplies, reports the outcome, refreshes the list and closes — the
 * block every list with a delete row action used to hand-roll.
 */

import React, { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

export interface ConfirmDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  /** The record's name, as the reader knows it. */
  name: string;
  /** Deletes the record. */
  onDelete: () => Promise<unknown>;
}

export const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({ open, onClose, name, onDelete }) => {
  const [deleting, setDeleting] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete();
      notify(t('ra.notification.deleted', { smart_count: 1 }));
      refresh();
      onClose();
    } catch {
      notify(t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{t('ra.message.delete_title', { name, id: '' })}</DialogTitle>
      <DialogContent>
        <Typography>{t('ra.message.delete_content')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={deleting}>
          {t('ra.action.cancel')}
        </Button>
        <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
          {t('ra.action.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
